// Language-tag normalization for the journey.
//
// Publishers (k-ID among them) pass a language code into the session and
// the backend echoes it back as `settings.language`. The codes arrive in
// every shape BCP 47 allows and a few it does not: `zh-TW`, `pt_br`,
// `NO`, `es-MX`, `sr-Latn`, `nn-NO`, an empty string, a number. This
// module turns any of those into one of the locale keys the app hosts
// (`src/locales/<flow>/<key>.json`), in a fixed order:
//
//   1. exact match (case-insensitive, `_` and `-` both accepted)
//   2. script / region alias (zh-TW → zh-Hant, no → nb, es-MX → es-419 …)
//   3. base language (pt-BR → pt, de-AT → de)
//   4. English
//
// Invalid or empty input goes to English and is logged once per value.
//
// `resolveLocaleKey` is pure so it can be unit-tested against any set of
// available keys; `useLocale` feeds it the keys of the flow being rendered.

import { trackEvent } from "./telemetry";

export const DEFAULT_LOCALE_KEY = "en";

// Keys are lowercase, `-`-separated tags (the resolver canonicalizes the
// input before looking here). Values are the locale keys we host, in
// their on-disk casing.
//
// Chinese is one language in two scripts. Traditional (`zh-Hant`) is
// served to Taiwan, Hong Kong and Macau; Simplified (`zh`) to the
// mainland and Singapore. A bare `zh` stays Simplified (the existing
// file), which matches what every publisher sent before Traditional
// existed.
//
// Norwegian: `no` is the macrolanguage code; Bokmål (`nb`) is what we
// translate. Nynorsk (`nn`) resolves to its own file when one exists
// and otherwise to Bokmål (see ALIAS_FALLBACKS).
//
// Latin American Spanish: the `es-419` file is the regional variant for
// every Spanish-speaking country in the Americas, so a country tag from
// that region lands there rather than on Castilian `es`.
export const LANGUAGE_ALIASES = {
  // Chinese, Traditional script
  "zh-tw": "zh-Hant",
  "zh-hk": "zh-Hant",
  "zh-mo": "zh-Hant",
  "zh-hant": "zh-Hant",
  "zh-hant-tw": "zh-Hant",
  "zh-hant-hk": "zh-Hant",
  "zh-hant-mo": "zh-Hant",
  // Chinese, Simplified script
  "zh-cn": "zh",
  "zh-sg": "zh",
  "zh-my": "zh",
  "zh-hans": "zh",
  "zh-hans-cn": "zh",
  "zh-hans-sg": "zh",
  // Norwegian
  no: "nb",
  "no-no": "nb",
  "nb-no": "nb",
  nn: "nn",
  "nn-no": "nn",
  // Serbian: Latin is the default script (see serbianTransliteration.js)
  sr: "sr-Latn-RS",
  "sr-latn": "sr-Latn-RS",
  "sr-latn-rs": "sr-Latn-RS",
  "sr-cyrl": "sr-RS",
  "sr-cyrl-rs": "sr-RS",
  "sr-rs": "sr-RS",
  // Montenegrin: ijekavian, informal register. PMI's Montenegro market
  // asked for its own wording (Oct 2026), so a Montenegro tag lands on
  // the sr-Latn-ME file where a flow hosts one and on Serbian Latin
  // otherwise (see ALIAS_FALLBACKS). `cnr` is the ISO 639-3 code.
  // `me` is PMI's own market code for Montenegro, not an ISO language
  // code, so accepting it collides with nothing.
  me: "sr-Latn-ME",
  "sr-me": "sr-Latn-ME",
  "sr-latn-me": "sr-Latn-ME",
  cnr: "sr-Latn-ME",
  "cnr-me": "sr-Latn-ME",
  "cnr-latn": "sr-Latn-ME",
  "cnr-latn-me": "sr-Latn-ME",
  // Latin American Spanish
  "es-419": "es-419",
  "es-mx": "es-419",
  "es-ar": "es-419",
  "es-bo": "es-419",
  "es-cl": "es-419",
  "es-co": "es-419",
  "es-cr": "es-419",
  "es-cu": "es-419",
  "es-do": "es-419",
  "es-ec": "es-419",
  "es-gt": "es-419",
  "es-hn": "es-419",
  "es-ni": "es-419",
  "es-pa": "es-419",
  "es-pe": "es-419",
  "es-pr": "es-419",
  "es-py": "es-419",
  "es-sv": "es-419",
  "es-us": "es-419",
  "es-uy": "es-419",
  "es-ve": "es-419",
  // Brazilian Portuguese (the file is named pt-BR; keep the alias so a
  // flow that hosts only `pt` still sees the base-language fallback run)
  "pt-br": "pt-BR",
  // Filipino / Tagalog
  fil: "tl",
  "fil-ph": "tl",
  "tl-ph": "tl",
  // Hebrew: `iw` is the pre-1989 ISO code Android still emits
  iw: "he",
  "iw-il": "he",
  // Indonesian: `in` is the legacy Java code
  in: "id",
  "in-id": "id",
};

// When an alias target is not hosted by the flow being rendered, try
// this next before dropping to the base language.
export const ALIAS_FALLBACKS = {
  nn: "nb",
  "zh-Hant": "zh",
  "sr-Latn-ME": "sr-Latn-RS",
};

/**
 * Canonical form of an incoming tag for lookups: trimmed, lowercased,
 * `_` → `-`, surrounding junk removed. Returns "" for anything that is
 * not a usable string.
 */
export function canonicalizeLanguageTag(input) {
  if (typeof input !== "string") return "";
  const tag = input.trim().replace(/_/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  if (!tag) return "";
  // A tag is letters and digits separated by hyphens; anything else
  // (spaces, slashes, a sentence) is not a language code.
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/i.test(tag)) return "";
  return tag.toLowerCase();
}

/**
 * BCP 47-style casing for the `lang` attribute: language lowercase,
 * script title-case, region uppercase. "zh-hant-tw" → "zh-Hant-TW".
 * Returns the canonical lowercase tag unchanged when it does not look
 * like language[-script][-region].
 */
export function formatLanguageTag(canonical) {
  if (!canonical) return "";
  return canonical
    .split("-")
    .map((part, i) => {
      if (i === 0) return part;
      if (part.length === 4) return part[0].toUpperCase() + part.slice(1);
      if (part.length === 2 || part.length === 3) return part.toUpperCase();
      return part;
    })
    .join("-");
}

const findKeyCaseInsensitive = (available, wanted) => {
  const target = wanted.toLowerCase();
  return available.find((key) => key.toLowerCase() === target) || null;
};

const warned = new Set();
const logFallback = (reason, input, key) => {
  const marker = `${reason}:${String(input)}`;
  if (warned.has(marker)) return;
  warned.add(marker);
  // eslint-disable-next-line no-console
  console.warn(
    `[locale] ${reason === "invalid" ? "Invalid" : "Unsupported"} language code ${JSON.stringify(
      input
    )} — falling back to "${key}"`
  );
  try {
    trackEvent("language_fallback", {
      reason,
      requested: typeof input === "string" ? input.slice(0, 32) : String(input),
      resolved: key,
    });
  } catch {
    // telemetry is best-effort
  }
};

/** Test hook: forget which inputs were already logged. */
export function _resetLanguageWarnings() {
  warned.clear();
}

/**
 * Resolve an incoming language tag to one of `available` locale keys.
 *
 * @param {unknown} input       the tag as received (settings.language)
 * @param {string[]} available  locale keys hosted by the flow (en.json etc.)
 * @param {{log?: boolean}} [options]  log=false silences the fallback warning
 * @returns {{ key: string, reason: string, canonical: string }}
 *   reason: "default" (empty input), "exact", "alias", "alias-fallback",
 *           "base", "invalid" (not a tag), "unsupported" (a tag we don't host)
 */
export function resolveLocaleKey(input, available = [], options = {}) {
  const keys = Array.isArray(available) ? available : Object.keys(available || {});
  const english = findKeyCaseInsensitive(keys, DEFAULT_LOCALE_KEY) || DEFAULT_LOCALE_KEY;
  const log = options.log !== false;

  if (input === undefined || input === null || input === "") {
    return { key: english, reason: "default", canonical: "" };
  }

  const canonical = canonicalizeLanguageTag(input);
  if (!canonical) {
    if (log) logFallback("invalid", input, english);
    return { key: english, reason: "invalid", canonical: "" };
  }

  if (canonical === DEFAULT_LOCALE_KEY) {
    return { key: english, reason: "exact", canonical };
  }

  // 1. exact (case-insensitive) match on the hosted key
  const exact = findKeyCaseInsensitive(keys, canonical);
  if (exact) return { key: exact, reason: "exact", canonical };

  // 2. script / region alias — on the full tag, then on each shorter
  //    prefix (zh-hant-tw → zh-hant → zh), so a tag with an extra
  //    subtag still finds its alias before the base-language step.
  const parts = canonical.split("-");
  for (let len = parts.length; len >= 1; len -= 1) {
    const prefix = parts.slice(0, len).join("-");
    const target = LANGUAGE_ALIASES[prefix];
    if (!target) continue;
    const hosted = findKeyCaseInsensitive(keys, target);
    if (hosted) return { key: hosted, reason: "alias", canonical };
    const fallback = ALIAS_FALLBACKS[target];
    const hostedFallback = fallback && findKeyCaseInsensitive(keys, fallback);
    if (hostedFallback) return { key: hostedFallback, reason: "alias-fallback", canonical };
    // the alias names a language this flow does not host: keep walking
    // the prefixes so the base language still gets its chance
  }

  // 3. base language
  const base = parts[0];
  if (base !== canonical) {
    const hostedBase = findKeyCaseInsensitive(keys, base);
    if (hostedBase) return { key: hostedBase, reason: "base", canonical };
  }

  // 4. English
  if (log) logFallback("unsupported", input, english);
  return { key: english, reason: "unsupported", canonical };
}

/**
 * Convenience: the resolved key alone.
 */
export function resolveLanguage(input, available, options) {
  return resolveLocaleKey(input, available, options).key;
}
